import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import SwipeableViews from 'react-swipeable-views-react-18-fix';
import { Box, Button, Grid, Typography } from '@mui/material';
import { Questions } from './Questions';
import Stepper from '../Stepper';
import { ChoiceInput } from 'components/checkIn/inputTypes/ChoiceInput';
import { CounterInput } from 'components/checkIn/inputTypes/CounterInput';
import { SlidingScale } from 'components/checkIn/inputTypes/SlidingScale';
import {
  flattenQuestionnaire,
  useQuestionnaireCopy,
} from 'components/checkIn/questionnaire/questionnaire';
import useRedirectToResults from 'components/checkIn/useRedirectToResults';
import { isValidAnswer } from 'services/checkIn/questionnaire-response';
import { questionAnswered, substanceUseQuestionnaireSkipped } from 'redux/checkIn/actions';
import { submitSurvey } from 'redux/checkIn/sagas';
import {
  getAnswer,
  selectAnswer,
  selectAnswers,
  selectCRAFFTScore,
  selectLastSubmissionFailure,
  selectLoading,
  selectSubmissionSuccessful,
  selectSubmittingSurvey,
} from 'redux/checkIn/selectors';
import { selectHiddenCards, selectProfile } from 'redux/reducers/session';
import { sendClientAnalytics } from 'redux/sagas/clientAnalyticsSaga';
import { sendUpdateProfile } from 'redux/sagas/updateProfileSaga';
import { AuditEventAction, createClientAnalytics } from 'ClientAnalytics';
import * as style from './QuestionPanel.style';

export const QuestionPanel = ({ setActiveTab }) => {
  const dispatch = useDispatch();

  const submissionSuccessful = useSelector(selectSubmissionSuccessful);
  const redirectToResults = useRedirectToResults();
  useEffect(() => {
    if (submissionSuccessful) redirectToResults();
  }, [submissionSuccessful, redirectToResults]);

  const { bscq, defaultQuestions } = useQuestionnaireCopy();
  const crafftScore = useSelector(selectCRAFFTScore);
  const hiddenCards = useSelector(selectHiddenCards);
  const profile = useSelector(selectProfile);
  const flattenedQuestions = useMemo(
    () => flattenQuestionnaire(defaultQuestions, hiddenCards),
    [defaultQuestions, hiddenCards],
  );
  const flattenedBscq = useMemo(() => flattenQuestionnaire(bscq, hiddenCards), [bscq, hiddenCards]);
  const allCards = useMemo(
    () => (crafftScore > 0 ? flattenedQuestions.concat(flattenedBscq) : flattenedQuestions),
    [crafftScore, flattenedQuestions, flattenedBscq],
  );

  const [activeStep, setActiveStep] = useState(0);
  const [currentCardIndex, setCurrentCardIndex] = useState(0);
  const maxSteps = allCards.filter((card) => card.type !== 'display').length;

  const handleStepChange = (step) => {
    setActiveStep(step);
  };

  const onNext = () => {
    let nextCardIndex = currentCardIndex + 1;
    const currentCard = allCards[currentCardIndex];
    if (currentCard?.type !== 'display') {
      if (activeStep < maxSteps) {
        setActiveStep((prevActiveStep) => prevActiveStep + 1);
      }
    }
    if (nextCardIndex < cards.length) {
      setCurrentCardIndex(nextCardIndex);
    }
  };

  const onPrevious = () => {
    let previousCardIndex = currentCardIndex - 1;
    const currentCard = allCards[previousCardIndex];
    if (currentCard?.type !== 'display') {
      if (activeStep >= 0) {
        setActiveStep((prevActiveStep) => prevActiveStep - 1);
      }
    }
    if (previousCardIndex >= 0) {
      setCurrentCardIndex(previousCardIndex);
    }
  };

  const handleSkip = () => {
    dispatch(substanceUseQuestionnaireSkipped());
    const lastCardIndex = cards.length - 1;
    setCurrentCardIndex(lastCardIndex);
    setActiveStep(maxSteps);
  };

  const handleDontShowAgain = () => {
    const cardsToHide =
      hiddenCards.length > 0
        ? hiddenCards + ',' + allCards[currentCardIndex].linkId
        : allCards[currentCardIndex].linkId;
    dispatch(sendUpdateProfile({ ...profile, hiddenCards: cardsToHide }));
  };

  const answers = useSelector(selectAnswers);
  const isSubmitting = useSelector(selectSubmittingSurvey);

  const isNextDisabled = () => {
    const currentCard = allCards[currentCardIndex];
    const isLastCard = currentCardIndex === cards.length - 1;
    const isQuestion = currentCard?.type !== 'display';
    const answer = getAnswer(answers, {
      linkId: currentCard?.linkId,
      groupLinkId: currentCard?.groupLinkId,
    });

    return isLastCard || (isQuestion && !isValidAnswer(answer)) || isSubmitting;
  };

  const isPreviousDisabled = () => {
    return currentCardIndex === 0 || isSubmitting;
  };

  useEffect(() => {
    setActiveTab(allCards[currentCardIndex]?.groupLinkId);
  }, [allCards, currentCardIndex, setActiveTab]);

  const cards = allCards
    .map((question, index) => (
      <QuestionCard
        key={question.linkId}
        question={question}
        index={index}
        isPreviousDisabled={isPreviousDisabled}
        isNextDisabled={isNextDisabled}
        onPrevious={onPrevious}
        onNext={onNext}
        handleSkip={handleSkip}
        handleDontShowAgain={handleDontShowAgain}
      />
    ))
    .concat(
      <SubmitCard
        key="submit-card"
        isPreviousDisabled={isPreviousDisabled}
        isNextDisabled={isNextDisabled}
        onPrevious={onPrevious}
        onNext={onNext}
      />,
    );

  useEffect(() => {
    if (currentCardIndex > cards.length - 1) setCurrentCardIndex(cards.length - 1);
  }, [currentCardIndex, cards.length]);

  return useSelector(selectLoading) ? (
    'Loading ...'
  ) : (
    <Box sx={style.container} data-testid="question-panel">
      <SwipeableViews
        disabled
        index={Math.min(currentCardIndex, cards.length - 1)}
        onChangeIndex={handleStepChange}
      >
        {cards}
      </SwipeableViews>
      <Stepper activeStep={activeStep} maxSteps={maxSteps} />
    </Box>
  );
};

const QuestionCard = ({
  question,
  index,
  isPreviousDisabled,
  isNextDisabled,
  onPrevious,
  onNext,
  handleSkip,
  handleDontShowAgain,
}) => {
  const dispatch = useDispatch();
  const intl = useIntl();

  const messages = defineMessages({
    dontShowAgainButton: {
      id: 'dont_show_again.button',
      defaultMessage: "Don't Show Again",
      description: "Don't show again button text",
    },
    skipSessionButton: {
      id: 'skip_session.button',
      defaultMessage: 'Skip This Section',
      description: 'Skip session button text',
    },
  });

  const { code, groupLinkId, linkId, type, groupText, text, options } = question;
  const params = useMemo(() => ({ groupLinkId, linkId }), [groupLinkId, linkId]);
  const answer = useSelector((state) => selectAnswer(state, params));
  const { getOptions } = useQuestionnaireCopy();
  let questionComponent = null;

  const onChange = (answer) => dispatch(questionAnswered(groupLinkId, linkId, answer));

  switch (type) {
    case 'display':
      if (code[0].code === 'SKIP') {
        questionComponent = (
          <Button variant="contained" onClick={handleSkip} sx={style.button}>
            {intl.formatMessage(messages.skipSessionButton)}
          </Button>
        );
      } else {
        questionComponent = (
          <Button variant="contained" onClick={handleDontShowAgain} sx={style.button}>
            {intl.formatMessage(messages.dontShowAgainButton)}
          </Button>
        );
      }
      break;
    case 'integer':
      questionComponent = (
        <SlidingScale
          thumbPosition={answer?.valueDecimal}
          startLabel={intl.formatMessage({
            id: 'sliding-scale.min-value-label',
            defaultMessage: 'No Pain',
          })}
          endLabel={intl.formatMessage({
            id: 'sliding-scale.max-value-label',
            defaultMessage: 'Severe Pain',
          })}
          onChange={(valueDecimal) => onChange({ valueDecimal })}
        />
      );
      break;
    case 'choice':
      const optionList = getOptions(options);
      questionComponent = (
        <ChoiceInput
          selection={answer?.valueDecimal}
          options={optionList}
          onChange={(valueDecimal) => onChange({ valueDecimal })}
        />
      );
      break;
    case 'decimal':
      questionComponent = (
        <CounterInput
          value={answer?.valueDecimal}
          unitLabel={
            answer?.valueDecimal === 1
              ? intl.formatMessage({
                  id: 'counter-input.unit-label',
                  defaultMessage: 'Day',
                })
              : intl.formatMessage({
                  id: 'counter-input.unit-label-plural',
                  defaultMessage: 'Days',
                })
          }
          onChange={(valueDecimal) => onChange({ valueDecimal })}
        />
      );
      break;
    default:
      questionComponent = null;
  }

  return (
    <Questions
      key={index}
      data-index={index}
      isQuestion={type !== 'display'}
      question={type !== 'display' ? groupText : ''}
      subQuestion={text}
      isPreviousDisabled={isPreviousDisabled}
      isNextDisabled={isNextDisabled}
      onPrevious={onPrevious}
      onNext={onNext}
    >
      {questionComponent}
    </Questions>
  );
};

const SubmitCard = ({ onPrevious, onNext, isPreviousDisabled, isNextDisabled }) => {
  const dispatch = useDispatch();
  const intl = useIntl();
  const isSubmitting = useSelector(selectSubmittingSurvey);
  const submissionFailure = useSelector(selectLastSubmissionFailure);

  const copy = defineMessages({
    blurb: {
      id: 'check-in.submit-card.blurb',
      defaultMessage: "Nice, you've answered all the questions!",
    },
    failureMessage: {
      id: 'check-in.submit-card.failure-message',
      defaultMessage: 'Failed to submit self-assessment.',
    },
    submitCaption: {
      id: 'check-in.submit-card.submit-caption',
      defaultMessage: 'Complete Assessment',
    },
    tryAgainCaption: {
      id: 'check-in.submit-card.try-again-caption',
      defaultMessage: 'Try Again',
    },
  });

  const onSubmit = useCallback(() => {
    dispatch(
      sendClientAnalytics(
        createClientAnalytics(
          AuditEventAction.CHECKIN_END.category,
          AuditEventAction.CHECKIN_END.action,
        ),
      ),
    );
    dispatch(submitSurvey());
  }, [dispatch]);

  return (
    <Questions
      isQuestion={false}
      isPreviousDisabled={isPreviousDisabled}
      isNextDisabled={isNextDisabled}
      onPrevious={onPrevious}
      onNext={onNext}
    >
      <Grid container direction="column" spacing={8}>
        <Grid item>
          <Typography typography="h4" sx={style.submitText}>
            {submissionFailure
              ? intl.formatMessage(copy.failureMessage)
              : intl.formatMessage(copy.blurb)}
          </Typography>
        </Grid>
        <Grid item>
          <Button variant="contained" onClick={onSubmit} disabled={isSubmitting} sx={style.button}>
            {submissionFailure
              ? intl.formatMessage(copy.tryAgainCaption)
              : intl.formatMessage(copy.submitCaption)}
          </Button>
        </Grid>
      </Grid>
    </Questions>
  );
};
